import { CheckIcon } from '@heroicons/react/24/solid';
import PropTypes from 'prop-types';

import useValidDeliveryDaysQuery from 'api/queries/useValidDeliveryDaysNoSubQuery';
import { BOYSENBERRY_S3_IMAGES } from 'constants';
import Card from 'honeydew/Card';
import useGlobalStore from 'hooks/useGlobalStore';
import { getSelectedDeliveryDay, getSignupLocalStorage } from 'utils/signupUtils';

export default function WatchYourImpact() {
    const signupLocalStorage = getSignupLocalStorage();
    const { isMisfitsTheme } = useGlobalStore();

    const { data: validDeliveryDays } = useValidDeliveryDaysQuery({
        extendedQueryKeys: ['signup'],
        funnelType: isMisfitsTheme ? 'misfits' : 'imperfect',
        zip: signupLocalStorage?.zip,
        orderInterval: signupLocalStorage?.orderInterval,
    });

    const selectedDeliveryDay = getSelectedDeliveryDay({ validDeliveryDays });

    const { freeShippingThreshold, isDeliveredByImperfect } = selectedDeliveryDay ?? {};

    return (
        <Card extendedStyles={{ padding: 'px-20 py-25' }} marginClasses={window.isNativeApp ? 'mb-[100px]' : ''}>
            <div className="flex flex-col gap-20">
                <div data-optimizely-id="watch-your-impact">
                    <h2 className="text-body-lg font-grotesk-bold">Watch your impact grow!</h2>
                    <p className="text-body-md">
                        <span className="font-grotesk-bold">
                            In 2023, our customers purchased over 500,000 lbs of food on average each week that would
                            have gone to waste or lesser outcomes{' '}
                        </span>
                        and saved an average of 4 lbs of greenhouse gas emissions and 189 gallons of water with each
                        order. The more you shop, the more you save!
                    </p>
                    <img
                        alt={isMisfitsTheme ? 'Assorted groceries' : 'Assorted veggies'}
                        src={`${BOYSENBERRY_S3_IMAGES}signup/${
                            isMisfitsTheme ? 'mm-grocery-banner.png' : 'if-veggie-banner.png'
                        }`}
                        className="w-full"
                    />
                </div>
                <div data-optimizely-id="why-were-different">
                    <h3 className="font-grotesk-bold text-body-mobile-lg">Why we&apos;re different</h3>
                    <ul className="md:columns-2">
                        <ListItem text="Skip an order, pause, or cancel anytime" />
                        {isDeliveredByImperfect && (
                            <ListItem text="Free Packaging Return Program" subText="(so long, gel pack guilt!)" />
                        )}
                        {freeShippingThreshold && (
                            <ListItem text={`Orders over $${freeShippingThreshold} ship for free`} />
                        )}
                        <ListItem text="Devoted Customer Care Team" />
                    </ul>
                </div>
            </div>
        </Card>
    );
}

function ListItem({ subText, text }) {
    return (
        <li className="mt-20 flex items-start gap-10 first:mt-0">
            <CheckIcon className="mb-5 h-25 w-25" />
            <p className="font-grotesk-bold text-body-mobile-md">
                {text}
                {subText && (
                    <>
                        <br />
                        <span className="font-grotesk">{subText}</span>
                    </>
                )}
            </p>
        </li>
    );
}

ListItem.propTypes = {
    subText: PropTypes.string,
    text: PropTypes.string.isRequired,
};

ListItem.defaultProps = {
    subText: null,
};
